import { emptySubject, hierarchyExpression } from './engine';
import type { GovernancePolicy, PolicyAsset, PolicyRule, SubjectSelector } from './types';

export interface RuleSuggestion {
  id: string;
  title: string;
  reason: string;
  confidence: number;
  rule: PolicyRule;
  expression?: string;
}

export function suggestRules(policy: GovernancePolicy, assets: PolicyAsset[]): { analysis: string; suggestions: RuleSuggestion[] } {
  const piiColumns = assets.flatMap((asset) => asset.columns.filter((column) => column.classification === 'PII').map((column) => `${asset.name}.${column.name}`));
  const restricted = assets.filter((asset) => asset.classification === 'Restricted' || asset.columns.some((column) => column.classification === 'Restricted'));
  const hasRegion = assets.some((asset) => asset.columns.some((column) => column.name === 'region'));
  const hasTenant = assets.some((asset) => asset.columns.some((column) => column.name === 'tenant_id'));
  const hasEmployee = assets.some((asset) => asset.columns.some((column) => column.name === 'employee_id'));
  const owner = policy.scope.level === 'VAULT' ? 'WorkSpace' : policy.scope.level === 'MODEL' ? 'data model' : 'asset';
  const analysis = `Reviewed ${assets.length} assets in this ${owner}. Found ${piiColumns.length} PII columns${restricted.length ? ` and ${restricted.length} Restricted assets` : ''}. ${policy.rules.length} rule${policy.rules.length === 1 ? '' : 's'} already drafted.`;

  const subject = (mode: SubjectSelector['mode'], extra: Partial<SubjectSelector> = {}): SubjectSelector => ({
    ...emptySubject(),
    mode,
    ...extra,
  });

  if (policy.type === 'ACCESS_CONTROL') {
    return {
      analysis,
      suggestions: [
        {
          id: 'ask-external-deny',
          title: 'Deny External consumers Read',
          reason: piiColumns.length ? `PII is present on ${piiColumns.slice(0, 3).join(', ')}. External users should not read these assets.` : 'External consumers should not read this scope by default.',
          confidence: 94,
          rule: { id: `ask-${Date.now()}-1`, subject: subject('groups', { groups: ['External'] }), action: { type: 'ACCESS', effect: 'deny', operations: ['SELECT'] } },
        },
        {
          id: 'ask-contractor-write',
          title: 'Block contractor writes',
          reason: 'Contractors typically need time-boxed read access only. Deny Write, Update, and Delete.',
          confidence: 88,
          rule: { id: `ask-${Date.now()}-2`, subject: subject('groups', { groups: ['Contractors'] }), action: { type: 'ACCESS', effect: 'deny', operations: ['INSERT', 'UPDATE', 'DELETE'] } },
        },
        {
          id: 'ask-hr-payroll',
          title: 'Allow HR to read Restricted assets',
          reason: restricted.length ? `${restricted.map((asset) => asset.name).join(', ')} are Restricted. Grant HR Read so payroll work is not blocked.` : 'Grant HR Read for Restricted assets that may appear in this WorkSpace.',
          confidence: 86,
          rule: { id: `ask-${Date.now()}-3`, subject: subject('groups', { groups: ['HR'] }), action: { type: 'ACCESS', effect: 'allow', operations: ['SELECT'] } },
        },
      ],
    };
  }

  if (policy.type === 'COLUMN_LEVEL' || policy.type === 'SENSITIVE_DATA') {
    return {
      analysis,
      suggestions: [
        {
          id: 'ask-mask-external',
          title: 'Full-mask PII for External users',
          reason: piiColumns.length ? `Mask ${piiColumns.slice(0, 2).join(' and ')} for the External group.` : 'Mask PII columns for External users when they appear.',
          confidence: 93,
          rule: { id: `ask-${Date.now()}-1`, subject: subject('groups', { groups: ['External'] }), action: { type: policy.type === 'SENSITIVE_DATA' ? 'SENSITIVE' : 'COLUMN', mask: { kind: 'full_string' } } },
        },
        {
          id: 'ask-email-analyst',
          title: 'Partial-mask email for Analysts',
          reason: 'Analysts often need to join on identity without seeing the full address.',
          confidence: 90,
          rule: { id: `ask-${Date.now()}-2`, subject: subject('roles', { roles: ['analyst'] }), action: { type: policy.type === 'SENSITIVE_DATA' ? 'SENSITIVE' : 'COLUMN', mask: { kind: 'partial_email' } } },
        },
        {
          id: 'ask-deny-restricted',
          title: 'Deny Restricted columns except HR',
          reason: 'Payroll and bank fields should stay hidden from everyone except HR.',
          confidence: 87,
          rule: { id: `ask-${Date.now()}-3`, subject: subject('attributes', { attribute: { key: 'department', operator: 'neq', value: 'HR' } }), action: { type: policy.type === 'SENSITIVE_DATA' ? 'SENSITIVE' : 'COLUMN', mask: { kind: 'deny_column' } } },
        },
      ],
    };
  }

  return {
    analysis,
    suggestions: [
      hasRegion || hasTenant
        ? {
            id: 'ask-region-tenant',
            title: 'Limit rows to caller tenant and region',
            reason: `${hasTenant ? 'tenant_id' : ''}${hasTenant && hasRegion ? ' and ' : ''}${hasRegion ? 'region' : ''} exist on selected tables.`,
            confidence: 95,
            rule: { id: `ask-${Date.now()}-1`, subject: subject('everyone'), action: { type: 'ROW_FILTER' }, expression: 'tenant_id = ${user.tenant_id} AND region = ${user.region}' },
            expression: 'tenant_id = ${user.tenant_id}\nAND region = ${user.region}',
          }
        : {
            id: 'ask-region',
            title: 'Limit rows to the caller region',
            reason: 'A region predicate is the safest default isolation rule for this scope.',
            confidence: 84,
            rule: { id: `ask-${Date.now()}-1`, subject: subject('everyone'), action: { type: 'ROW_FILTER' }, expression: 'region = ${user.region}' },
            expression: 'region = ${user.region}',
          },
      hasEmployee
        ? {
            id: 'ask-hierarchy',
            title: 'Let managers see their reporting chain',
            reason: 'employee_id is available. Use the synthetic hierarchy so managers see themselves and descendants.',
            confidence: 89,
            rule: { id: `ask-${Date.now()}-2`, subject: subject('roles', { roles: ['manager'] }), action: { type: 'ROW_FILTER' }, expression: hierarchyExpression() },
            expression: hierarchyExpression(),
          }
        : {
            id: 'ask-manager',
            title: 'Show only rows owned by the caller',
            reason: 'No employee hierarchy column was found. Fall back to account manager isolation.',
            confidence: 80,
            rule: { id: `ask-${Date.now()}-2`, subject: subject('everyone'), action: { type: 'ROW_FILTER' }, expression: 'account_manager_id = ${user.employee_id}' },
            expression: 'account_manager_id = ${user.employee_id}',
          },
    ],
  };
}

export function suggestionKey(rule: PolicyRule): string {
  return `${rule.subject.mode}:${rule.subject.roles[0] ?? rule.subject.groups[0] ?? ''}:${rule.action.type}:${'effect' in rule.action ? rule.action.effect : 'mask' in rule.action ? rule.action.mask.kind : rule.expression ?? ''}`;
}
